import { useFind } from "../../../state/reducers/hooks"
import { TableName } from "../../../tables"
import { define, isDefined, round } from "../../../utils/typeUtils"
import { useAdministrationHooks, usePaymentTypeLoaders, useTaxHooks, useTaxLoaders } from "../../administration"
import { Discount } from "../../common"
import { Product, ProductPrices, useProductLoaders } from "../../products"
import { usePurchasesHooks } from "../../purchases"
import { PricesList, useCustomerTypeLoaders } from "../../sales"
import { Item } from "../state/types"
import { SalePriceItem } from "./types"

export const usePricesHooks = () => {
    const { getParentSaleYield } = useAdministrationHooks()
    const { getTotalSaleTax } = useTaxHooks()
    const { getSettings } = usePurchasesHooks()
    const find = useFind()

    const productLoaders = useProductLoaders()
    const customerTypeLoaders = useCustomerTypeLoaders()
    const paymentTypeLoaders = usePaymentTypeLoaders()
    const taxLoaders = useTaxLoaders()

    const getProduct = (id: string) => define(find(TableName.PRODUCTS, id)) as Product

    const multiplier = (): number => 1 - (getTotalSaleTax() / 100)

    const calculateAdjustmentAmount = (base: number, yieldPercentage: number) => round(base * yieldPercentage / 100)

    const applyAdjustment = (base: number, yieldPercentage: number) => base + calculateAdjustmentAmount(base, yieldPercentage)

    const removeAdjustment = (adjustedAmount: number, yieldPercentage: number) => round(adjustedAmount / (1 + (yieldPercentage / 100)))

    const calculateYield = (finalPrice: number, basePrice: number) => ((finalPrice / basePrice) - 1) * 100

    const adjustment = {
        calculate: calculateAdjustmentAmount,
        apply: applyAdjustment,
        remove: removeAdjustment,
        calculateYield
    }

    const getProductPrices = (productId: string) => define(find(TableName.PRODUCT_PRICES, productId, 'productId')) as ProductPrices
    
    const getProductPurchasePrice = (productId: string): number => {
        const productPrices = getProductPrices(productId)
        return getPurchasePrice(productPrices.purchasePrice, productPrices.bonuses)
    }

    // The list price comes from the purchase price: a product is for sale once purchases loaded it
    const isProductForSale = (productId: string): boolean => {
        const productPrices = find(TableName.PRODUCT_PRICES, productId, 'productId') as ProductPrices | undefined
        return !!productPrices && getPurchasePrice(productPrices.purchasePrice, productPrices.bonuses) > 0
    }

    const getVatTaxId = (productId: string) => getProductPrices(productId).vatTaxId

    const getVatTaxYield = (vatTaxId: string) => {
        const { findTax } = taxLoaders()
        const vatTax = findTax(vatTaxId)!
        return vatTax.yield
    }

    const getProductVatTaxYield = (productId: string) => {
        const vatTaxId = getVatTaxId(productId)
        return getVatTaxYield(vatTaxId)
    }

    const getCustomerTypeYield = (
        customerTypeId: string,
        productId: string
    ): number => {
        const { findCustomerType } = customerTypeLoaders()
        const customerType = findCustomerType(customerTypeId)!
        const productYield = customerType.productYields.find(productYield => productYield.productId === productId)
        const ownYield = isDefined(productYield?.yield) ? productYield!.yield : customerType.yield
        const baseCustomerType = customerType.baseCustomerTypeId && findCustomerType(customerType.baseCustomerTypeId)
        if (!baseCustomerType) return ownYield
        // Una lista basada en otra aplica su ajuste sobre el precio de la lista base (igual que getProductAdjustedPrice)
        const baseYield = getCustomerTypeYield(baseCustomerType.id, productId)
        return ((1 + baseYield / 100) * (1 + ownYield / 100) - 1) * 100
    }

    const getBasePrice = (purchasePrice: number, saleYieldParam?: number, vatTaxId?: string): number => {
        const generalSaleYield = define(getSettings()?.generalSaleYield) as number
        const saleYield = saleYieldParam || generalSaleYield
        const saleYieldMultiplier = 1 + (saleYield / 100)
        const vatTaxYieldMultiplier = vatTaxId ? 1 + (getVatTaxYield(vatTaxId) / 100) : 1
        return round(purchasePrice * saleYieldMultiplier * vatTaxYieldMultiplier / multiplier())
    }

    const getPurchaseBasePrice = (productId: string): number => getProductPrices(productId).purchasePrice

    const getPurchasePrice = (basePrice: number, bonuses: Discount[]): number => {
        const bonusAmounts = bonuses.map(bonus => basePrice * bonus.yield / 100)
        return basePrice - bonusAmounts.reduce((acc, bonusAmount) => acc + bonusAmount, 0)
    }

    const getSaleYield = (purchasePrice: number, basePrice: number): number => {
        if (purchasePrice === 0) return 0
        const saleYieldCent = (basePrice / purchasePrice) * multiplier() - 1
        return saleYieldCent * 100
    }

    const getProductBasePrice = (productId: string, withVat = false): number => {
        const purchasePrice = getProductPurchasePrice(productId)
        const parentSaleYield = getParentSaleYield(getProduct(productId).categoryId)
        const saleYieldParam = getProductPrices(productId).saleYield || parentSaleYield
        const saleYieldMultiplier = 1 + (saleYieldParam! / 100)
        const vatTaxYieldMultiplier = withVat ? 1 + (getProductVatTaxYield(productId) / 100) : 1
        return round(purchasePrice * saleYieldMultiplier * vatTaxYieldMultiplier / multiplier())
    }

    const getProductAdjustedPrice = (
        productId: string,
        withVat = false,
        customerTypeId?: string,
        paymentTypeId?: string,
    ): number => {
        if (!customerTypeId) {
            return getProductBasePrice(productId, withVat);
        }
        const customerType = customerTypeLoaders().findCustomerType(customerTypeId);
        if (!customerType) return getProductBasePrice(productId, withVat);
        let priceToAdjust: number;
        if (customerType.baseCustomerTypeId) {
            priceToAdjust = getProductAdjustedPrice(productId, withVat, customerType.baseCustomerTypeId);
        } else {
            priceToAdjust = getProductBasePrice(productId, withVat);
        }
        const productYieldItem = customerType.productYields?.find(py => py.productId === productId);
        const currentYield = isDefined(productYieldItem?.yield) ? productYieldItem!.yield : (customerType.yield || 0);
        const customerTypeAdjustedPrice = applyAdjustment(priceToAdjust, currentYield);
        const paymentType = paymentTypeLoaders().findPaymentType(paymentTypeId);
        const finalPrice = paymentType ? applyAdjustment(customerTypeAdjustedPrice, paymentType.yield) : customerTypeAdjustedPrice;

        return round(finalPrice);
    }

    const getPricesList = async (customerTypeId?: string): Promise<PricesList> => {
        const products = await productLoaders().loadActiveProducts()
        const pricesList: PricesList = {}
        products.forEach(product => {
            pricesList[product.id] = getProductAdjustedPrice(product.id, true, customerTypeId)
        })
        return pricesList
    }

    const getPricesListWithoutVat = async (
        pricesListWithVat?: PricesList,
        customerTypeId?: string
    ): Promise<PricesList> => {
        const pricesList = pricesListWithVat || await getPricesList(customerTypeId)
        const pricesListWithoutVat: PricesList = {}
        Object.keys(pricesList).forEach(productId => {
            pricesListWithoutVat[productId] = removeVat(pricesList[productId], getVatTaxId(productId))
        })
        return pricesListWithoutVat
    }

    const applyVat = (basePrice: number, vatTaxId: string): number => {
        const vatTaxYield = getVatTaxYield(vatTaxId)
        return applyAdjustment(basePrice, vatTaxYield)
    }

    const removeVat = (adjustedAmount: number, vatTaxId: string): number => {
        const vatTaxYield = getVatTaxYield(vatTaxId)
        return removeAdjustment(adjustedAmount, vatTaxYield)
    }

    const product = {
        vatTaxId: getVatTaxId,
        vatTaxYield: getProductVatTaxYield,
        purchaseBasePrice: getPurchaseBasePrice,
        purchasePrice: getProductPurchasePrice,
        isForSale: isProductForSale,
        basePrice: (productId: string, withVat = false, customerTypeId?: string) =>
        getProductAdjustedPrice(productId, withVat, customerTypeId),
        customerTypeYield: getCustomerTypeYield,
        adjustedPrice: getProductAdjustedPrice,
        pricesList: getPricesList,
        pricesListWithoutVat: getPricesListWithoutVat,
        applyVat,
        removeVat
    }

    const getSaleFullPriceFromItems = (items: SalePriceItem[]): number => {
        if (items.length === 0) {
            return 0
        }

        let fullPriceSum = 0
        items.forEach(item => {
            fullPriceSum = fullPriceSum + ((item.price || 0) * item.quantity)
        })

        return round(fullPriceSum)
    }

    const getHoardDeliveredCredit = (
        items: Partial<Item>[]
    ): number => {
        const priceItems = items.map(item => ({
            price: item.price,
            quantity: item.quantity
        })) as SalePriceItem[]
        
        return getSaleFullPriceFromItems(priceItems)
    }

    const getHoardRemainingCredit = (
        hoardValue: number,
        items: Partial<Item>[]
    ): number => {
        return hoardValue - getHoardDeliveredCredit(items)
    }

    const hoard = {
        deliveredCredit: getHoardDeliveredCredit,
        remainingCredit: getHoardRemainingCredit
    }

    return {
        adjustment,
        basePrice: getBasePrice,
        purchasePrice: getPurchasePrice,
        saleYield: getSaleYield,
        product,
        hoard
    }
}
